import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DynamicPage, DynamicPageTitle, Title, Bar, Button, Select, Option, Input, Label,
  Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell,
  MessageStrip, Text, BusyIndicator, FlexBox, ObjectStatus, ProgressIndicator,
} from '@ui5/webcomponents-react';
import { api, type Rec } from '../api/client';
import { useIsMobile } from '../hooks/useIsMobile';

const money = (n: number, c?: string) => `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${c ? ' ' + c : ''}`;

const GROUPINGS = [
  { key: 'account', label: 'G/L account' },
  { key: 'costCentre', label: 'Cost centre' },
  { key: 'costCentre2', label: 'Line of business' },
  { key: 'project', label: 'Project' },
  { key: 'cardCode', label: 'Business partner' },
] as const;

/**
 * Budget vs actual. Budget comes from the budget lines of a version; actuals are aggregated from real SAP
 * journal entries. The actuals are cached and synced incrementally (the Service Layer cannot aggregate, and
 * returns journal lines inline at ~6 KB per entry), so the page shows how fresh they are and lets the user
 * sync on demand rather than pretending the number is always live.
 */
export function BudgetReport() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const isMobile = useIsMobile();

  const version = sp.get('version') ?? '';
  const groupBy = (sp.get('groupBy') ?? 'account') as typeof GROUPINGS[number]['key'];
  const fromPeriod = Number(sp.get('fromPeriod') ?? 1);
  const toPeriod = Number(sp.get('toPeriod') ?? 12);
  const setParam = (k: string, v: string) => setSp(p => { const n = new URLSearchParams(p); v ? n.set(k, v) : n.delete(k); return n; }, { replace: true });

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ design: 'Positive' | 'Negative' | 'Information'; text: string } | null>(null);

  // Budget versions come from the generic data API — they are just rows in the @BG_VERSION UDT.
  const versions = useQuery({
    queryKey: ['budget-versions'],
    queryFn: () => api.list('U_BG_VERSION', { select: 'Code,Name,U_Year,U_Status,U_Currency', orderby: 'Code desc', top: 100 }),
  });
  const versionRows = (versions.data?.value ?? []) as Rec[];

  const report = useQuery({
    queryKey: ['budget-variance', version, groupBy, fromPeriod, toPeriod],
    queryFn: () => api.budgetVariance({ version, groupBy, fromPeriod, toPeriod }),
    enabled: !!version,
  });
  const data = report.data;

  const sync = async (rebuild: boolean) => {
    setBusy(true); setMsg(null);
    try {
      // A first sync on a company with years of history needs several passes; keep going while more remains.
      let scanned = 0, passes = 0;
      let r = rebuild ? await api.rebuildBudgetActuals() : await api.syncBudgetActuals();
      scanned += r.scanned; passes++;
      while (r.more && passes < 25) { r = await api.syncBudgetActuals(); scanned += r.scanned; passes++; }
      setMsg({
        design: 'Positive',
        text: r.more
          ? `Synced ${scanned.toLocaleString()} journal entries and there are still more — run sync again to continue.`
          : `Up to date — ${scanned.toLocaleString()} new journal entr${scanned === 1 ? 'y' : 'ies'} folded in.`,
      });
      await report.refetch();
    } catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); }
    finally { setBusy(false); }
  };

  // Counted apart, because they are opposite failures: one overspent, the other under-sold.
  const worst = useMemo(() => (data?.rows ?? []).filter(r => r.overBudget).length, [data]);
  const missed = useMemo(() => (data?.rows ?? []).filter(r => r.underTarget).length, [data]);

  const actions = <>
    <Button icon="refresh" disabled={busy} onClick={() => sync(false)}>{busy ? 'Syncing…' : 'Sync actuals'}</Button>
    <Button icon="synchronize" design="Transparent" disabled={busy}
      onClick={() => { if (window.confirm('Discard the cached actuals and rescan every journal entry from the start? The figures are rebuilt from SAP, so nothing is lost — it just takes longer.')) sync(true); }}>
      Rebuild
    </Button>
    <Button design="Emphasized" icon="add" onClick={() => nav('/s/bg-versions/new')}>New version</Button>
  </>;

  return (
    <DynamicPage
      titleArea={<DynamicPageTitle
        heading={<Title level="H3">Budget vs Actual</Title>}
        actionsBar={isMobile ? undefined : <Bar design="Subheader" endContent={actions} />} />}
      showFooter={isMobile} footerArea={isMobile ? <Bar design="Footer" endContent={actions} /> : undefined}>

      {msg && <MessageStrip design={msg.design} onClose={() => setMsg(null)} style={{ marginBottom: '0.5rem' }}>{msg.text}</MessageStrip>}

      <FlexBox wrap="Wrap" style={{ gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-end' }}>
        <FlexBox direction="Column" style={{ minWidth: '14rem' }}>
          <Label>Budget version</Label>
          <Select onChange={e => setParam('version', String(e.detail.selectedOption.dataset.key ?? ''))}>
            <Option data-key="">— choose —</Option>
            {versionRows.map(v => (
              <Option key={String(v.Code)} data-key={String(v.Code)} selected={String(v.Code) === version}>
                {String(v.Name ?? v.Code)} ({String(v.U_Year ?? '')}{v.U_Status ? ` · ${String(v.U_Status)}` : ''})
              </Option>
            ))}
          </Select>
        </FlexBox>
        <FlexBox direction="Column" style={{ minWidth: '11rem' }}>
          <Label>Group by</Label>
          <Select onChange={e => setParam('groupBy', String(e.detail.selectedOption.dataset.key ?? 'account'))}>
            {GROUPINGS.map(g => <Option key={g.key} data-key={g.key} selected={g.key === groupBy}>{g.label}</Option>)}
          </Select>
        </FlexBox>
        <FlexBox direction="Column" style={{ width: '7rem' }}>
          <Label>From period</Label>
          <Input type="Number" value={String(fromPeriod)} onChange={e => setParam('fromPeriod', e.target.value ?? '1')} />
        </FlexBox>
        <FlexBox direction="Column" style={{ width: '7rem' }}>
          <Label>To period</Label>
          <Input type="Number" value={String(toPeriod)} onChange={e => setParam('toPeriod', e.target.value ?? '12')} />
        </FlexBox>
      </FlexBox>

      {!version && <MessageStrip design="Information" hideCloseButton>Choose a budget version to compare against actual postings.</MessageStrip>}

      {version && report.isLoading && <BusyIndicator active style={{ margin: '2rem auto', display: 'block' }} />}
      {version && report.isError && <MessageStrip design="Negative" hideCloseButton>{(report.error as Error).message}</MessageStrip>}

      {data && (
        <>
          {data.note && <MessageStrip design="Critical" hideCloseButton style={{ marginBottom: '0.5rem' }}>{data.note}</MessageStrip>}

          {/* A budget line pinning a dimension nothing posts can never match. Without this the report looks
              clean while the commitment it states is never actually tested. */}
          {(data.warnings ?? []).map((w, i) => (
            <MessageStrip key={i} design="Critical" hideCloseButton style={{ marginBottom: '0.5rem' }}>{w}</MessageStrip>
          ))}

          <FlexBox wrap="Wrap" style={{ gap: '1.5rem', marginBottom: '0.75rem' }}>
            {/* Spend and revenue are never added together: a sales target plus a cost ceiling is not a
                budget. They are shown as two pairs, each read in its own direction. */}
            {(!!data.totals.expense.budget || !!data.totals.expense.actual) && <>
              <Summary label="Spend plan" value={money(data.totals.expense.budget, data.version.currency)} />
              <Summary label="Spent" value={money(data.totals.expense.actual, data.version.currency)}
                state={data.totals.expense.variance < 0 ? 'Negative' : 'Positive'} />
            </>}
            {(!!data.totals.revenue.budget || !!data.totals.revenue.actual) && <>
              <Summary label="Revenue target" value={money(data.totals.revenue.budget, data.version.currency)} />
              <Summary label="Booked" value={money(data.totals.revenue.actual, data.version.currency)}
                state={data.totals.revenue.variance > 0 ? 'Negative' : 'Positive'} />
            </>}
            <Summary label="Lines over budget" value={String(worst)} state={worst ? 'Critical' : 'Positive'} />
            {!!missed && <Summary label="Revenue lines short" value={String(missed)} state="Critical" />}
            {!!data.unmatched?.buckets && (
              // A subset of Actual, not an addition to it: spend on a budgeted account that no line governs.
              <Summary label="Of the actual, no line governs" state="Critical"
                value={money(data.unmatched.actual, data.version.currency)} />
            )}
            {!!data.unbudgeted?.buckets && (
              <Summary label="Unbudgeted spend (not in totals)"
                value={money(data.unbudgeted.actual, data.version.currency)} />
            )}
          </FlexBox>

          <Text style={{ display: 'block', fontSize: '0.8rem', color: 'var(--sapNeutralTextColor)', marginBottom: '0.5rem' }}>
            {data.version.name ?? data.version.code} · FY {data.version.year} · periods {data.fromPeriod}–{data.toPeriod}
            {data.actualsAsOf
              ? ` · actuals synced ${new Date(data.actualsAsOf).toLocaleString()} (to journal ${data.actualsLastJdt.toLocaleString()})`
              : ' · actuals never synced'}
          </Text>

          <Table>
            <TableHeaderRow slot="headerRow">
              <TableHeaderCell><span>{GROUPINGS.find(g => g.key === data.groupBy)?.label}</span></TableHeaderCell>
              <TableHeaderCell horizontalAlign="End"><span>Budget</span></TableHeaderCell>
              <TableHeaderCell horizontalAlign="End"><span>Actual</span></TableHeaderCell>
              <TableHeaderCell horizontalAlign="End"><span>Variance</span></TableHeaderCell>
              <TableHeaderCell horizontalAlign="End"><span>Used</span></TableHeaderCell>
            </TableHeaderRow>
            {data.rows.map(r => {
              // Percent of budget consumed. Guarded: a zero budget with real spend is "over", not infinity.
              const used = r.budget ? Math.round((r.actual / r.budget) * 100) : (r.actual ? 100 : 0);
              // A revenue row is read the other way up: past 100% of target is good, short of it is not.
              const bad = r.overBudget || r.underTarget;
              const usedState = r.kind === 'revenue'
                ? (used >= 100 ? 'Positive' : used >= 90 ? 'Critical' : 'Negative')
                : (used > 100 ? 'Negative' : used > 90 ? 'Critical' : 'Positive');
              return (
                <TableRow key={r.key}>
                  <TableCell>
                    <FlexBox direction="Column">
                      <Text>{r.label}</Text>
                      {r.label !== r.key && <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>{r.key}</Text>}
                      {r.kind !== 'expense' && (
                        <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>
                          {r.kind === 'revenue' ? 'revenue — target vs booked' : 'mixed revenue and spend'}
                        </Text>
                      )}
                    </FlexBox>
                  </TableCell>
                  <TableCell><Text style={{ width: '100%', textAlign: 'right' }}>{money(r.budget)}</Text></TableCell>
                  <TableCell><Text style={{ width: '100%', textAlign: 'right' }}>{money(r.actual)}</Text></TableCell>
                  <TableCell>
                    <ObjectStatus state={bad ? 'Negative' : 'Positive'} style={{ width: '100%', justifyContent: 'flex-end' }}>
                      {money(r.variance)}{r.variancePct !== null ? ` (${r.variancePct}%)` : ''}
                    </ObjectStatus>
                  </TableCell>
                  <TableCell>
                    <ProgressIndicator value={Math.max(0, Math.min(100, used))}
                      valueState={usedState}
                      displayValue={`${used}%`} style={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </Table>

          {!data.rows.length && (
            <MessageStrip design="Information" hideCloseButton style={{ marginTop: '0.75rem' }}>
              Nothing to compare yet — add budget lines to this version, and sync actuals so posted journals are counted.
            </MessageStrip>
          )}
        </>
      )}
    </DynamicPage>
  );
}

function Summary({ label, value, state }: { label: string; value: string; state?: 'Positive' | 'Negative' | 'Critical' }) {
  return (
    <FlexBox direction="Column">
      <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>{label}</Text>
      {state
        ? <ObjectStatus state={state} style={{ fontSize: '1.25rem' }}>{value}</ObjectStatus>
        : <Text style={{ fontSize: '1.25rem' }}>{value}</Text>}
    </FlexBox>
  );
}
